import { useRef } from "react"
import { Button, Center, Drawer, Loader, Text, Group, CloseButton } from "@mantine/core"
import { Trans, useTranslation } from "react-i18next"
import { notifications } from "@mantine/notifications"
import { useUser } from "../../user/hooks/useUser"
import { modals } from "@mantine/modals"
import { useAgencyVisit, useDeleteAgencyVisit } from "../hooks/useAgencyVisits"
import AgencyVisitDetailContent from "./AgencyVisitDetailContent"
import AgencyVisitFormContent from "./AgencyVisitFormContent"

export type DrawerMode = "view" | "create" | "edit"

interface Props {
  opened: boolean
  onClose: () => void
  mode: DrawerMode
  selectedVisitId: number | null
  onDeleted: () => void
  onSwitchToEdit: () => void
}

export default function AgencyVisitDrawer({ opened, onClose, mode, selectedVisitId, onDeleted, onSwitchToEdit }: Props) {
  const { t } = useTranslation()
  const { data: visit, isLoading } = useAgencyVisit(mode !== "create" ? selectedVisitId : null)
  const { data: userData } = useUser()
  const canManage = userData?.can?.manageAgencyVisits ?? false
  const deleteMutation = useDeleteAgencyVisit()
  const formRef = useRef<{ submit: () => void }>(null)
  const isFormMode = mode === "edit" || mode === "create"

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t("agencyVisits.confirmDelete"),
      children: <Text size="sm">{t("agencyVisits.confirmDeleteMessage")}</Text>,
      labels: { confirm: t("common.delete"), cancel: t("common.cancel") },
      confirmProps: { color: "red" },
      onConfirm: () => {
        deleteMutation.mutate(visit!.id, {
          onSuccess: () => {
            notifications.show({ color: "green", title: t("common.success"), message: t("agencyVisits.deleteSuccess") })
            onDeleted()
          },
        })
      },
    })
  }

  const title =
    mode === "create"
      ? t("agencyVisits.createTitle")
      : mode === "edit"
        ? t("agencyVisits.editTitle")
        : t("agencyVisits.title")

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Drawer.Title>
          <Text fw={600} size="md">{title}</Text>
        </Drawer.Title>
        <Group gap="xs">
          {mode === "view" && canManage && (
            <>
              <Button size="xs" variant="default" onClick={onSwitchToEdit} leftSection={<i className="fa fa-pencil" />}>
                <Trans i18nKey="common.edit" />
              </Button>
              <Button size="xs" color="red" onClick={handleDelete} leftSection={<i className="fa fa-trash" />}>
                <Trans i18nKey="common.delete" />
              </Button>
            </>
          )}
          {isFormMode && (
            <Button size="xs" onClick={() => formRef.current?.submit()}>
              <Trans i18nKey={mode === "edit" ? "common.update" : "common.create"} />
            </Button>
          )}
          <CloseButton onClick={onClose} />
        </Group>
      </Drawer.Header>

      {mode === "create" ? (
        <AgencyVisitFormContent ref={formRef} onSuccess={onClose} />
      ) : isLoading ? (
        <Center h="100%">
          <Loader />
        </Center>
      ) : !visit ? (
        <Text>
          <Trans i18nKey="common.notFound" />
        </Text>
      ) : mode === "edit" ? (
        <AgencyVisitFormContent ref={formRef} visit={visit} onSuccess={onClose} />
      ) : (
        <AgencyVisitDetailContent
          visit={visit}
        />
      )}
    </Drawer>
  )
}
